import { useMemo, useState } from "react";
import { Box, Typography, Dialog, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { useVisitPool, toISODate, addDays } from "./data";
import type { SfVisit } from "./data";
import { scoreVisit, fmtPoints } from "./points";
import { usePointsConfig } from "./pointsConfig";

/**
 * Day Planner — opened from an empty day, or one with room for a second visit.
 * Two tabs over the visit pool: this consultant's allocated work, and everything.
 * Both ordered by due date and nothing else, with allocation and location as
 * columns, so the choice stays with the person planning.
 */

export interface PlannerTarget {
    consultantName: string;
    email: string;
    day: Date;
    existing: SfVisit[]; // visits already on this day
}

const SHOWN = 25;

const fmtTime = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const btn = (variant: "dark" | "plain") => ({
    padding: "8px 14px",
    borderRadius: "8px",
    fontSize: "12.5px",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: T.font,
    whiteSpace: "nowrap" as const,
    border: variant === "plain" ? `1px solid ${T.border}` : "none",
    background: variant === "dark" ? T.ink : T.card,
    color: variant === "plain" ? T.muted : "#fff",
    "&:hover": { background: variant === "dark" ? T.indigoDeep : T.rowTint },
});

const GRID = "104px 1.5fr 1.2fr 52px 1fr";

const DayPlannerModal = ({
    target,
    onClose,
    onAddAdmin,
}: {
    target: PlannerTarget | null;
    onClose: () => void;
    onAddAdmin: () => void;
}) => {
    const { config } = usePointsConfig();

    // Hooks must run before the early return below.
    const day = target?.day ?? new Date();
    const pool = useVisitPool(toISODate(addDays(day, -365)), toISODate(addDays(day, 90)));

    const [tab, setTab] = useState<"mine" | "all">("mine");

    const rows = useMemo(() => {
        const alreadyHere = new Set((target?.existing ?? []).map((e) => e.id));
        return (pool.data ?? [])
            .filter((v) => !alreadyHere.has(v.id))
            .map((v) => ({
                v,
                points: scoreVisit(
                    {
                        subActivity: v.subActivity,
                        name: v.name,
                        visitCategory: v.visitCategory,
                        industry: v.industry,
                        fraRiskLevel: v.fraRiskLevel,
                        highRiskFra: v.highRiskFra,
                    },
                    config,
                    // Unbooked work has no On Site / Remote flag yet.
                    { assumeOnSiteIfUnset: true },
                ).points,
            }))
            .sort((a, b) => (a.v.dueDate || "").localeCompare(b.v.dueDate || ""));
    }, [pool.data, target, config]);

    const mine = useMemo(() => {
        const email = (target?.email || "").toLowerCase();
        return rows.filter((r) => (r.v.allocatedToEmail || "").toLowerCase() === email);
    }, [rows, target]);

    if (!target) return null;

    const shown = tab === "mine" ? mine : rows;
    const firstName = target.consultantName.split(" ")[0];

    const dateLabel = target.day.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long" });

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "14px", fontFamily: T.font, background: T.card } }}>
            <Box sx={{ padding: "20px 24px", fontFamily: T.font, color: T.ink2 }}>
                <Typography sx={{ fontSize: "17px", fontWeight: 800, color: T.ink2 }}>Plan this day</Typography>
                <Typography sx={{ fontSize: "12.5px", color: T.muted, mt: "2px" }}>
                    {target.consultantName} · {dateLabel}
                    {target.existing.length > 0 &&
                        ` · already has ${target.existing
                            .map((v) => `${v.accountName} ${fmtTime(v.visitStartDate)}`)
                            .join(", ")}`}
                </Typography>

                {pool.isLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: "28px" }}>
                        <CircularProgress size={22} />
                    </Box>
                )}
                {pool.isError && (
                    <Typography sx={{ fontSize: "12.5px", color: T.amber, mt: "16px" }}>
                        Couldn't load the visit pool.
                    </Typography>
                )}

                {!pool.isLoading && !pool.isError && (
                    <Box sx={{ mt: "16px", border: `1px solid ${T.border}`, borderRadius: "10px", overflow: "hidden" }}>
                        <Box sx={{ display: "flex", background: T.rowTint, borderBottom: `1px solid ${T.borderSoft}` }}>
                            {(
                                [
                                    { key: "mine", label: `Allocated to ${firstName}`, n: mine.length },
                                    { key: "all", label: "All visit pool", n: rows.length },
                                ] as const
                            ).map((t) => (
                                <Box
                                    key={t.key}
                                    component="button"
                                    onClick={() => setTab(t.key)}
                                    sx={{
                                        border: "none",
                                        borderBottom: `2px solid ${tab === t.key ? T.purple : "transparent"}`,
                                        background: tab === t.key ? T.card : "transparent",
                                        color: tab === t.key ? T.ink2 : T.muted,
                                        padding: "10px 15px",
                                        fontSize: "12.5px",
                                        fontWeight: 800,
                                        cursor: "pointer",
                                        fontFamily: T.font,
                                    }}
                                >
                                    {t.label}
                                    <Box component="span" sx={{ color: T.muted, fontWeight: 700, ml: "6px" }}>
                                        {t.n}
                                    </Box>
                                </Box>
                            ))}
                            <Typography sx={{ fontSize: "11.5px", color: T.muted, ml: "auto", alignSelf: "center", pr: "11px" }}>
                                Soonest due first
                            </Typography>
                        </Box>

                        <Box sx={{ overflowX: "auto" }}>
                            <Box sx={{ minWidth: "620px" }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: GRID, padding: "6px 11px", borderTop: `1px solid ${T.borderSoft}` }}>
                                    {["Due", "Account", "Visit type", "Pts", "Allocated to"].map((h) => (
                                        <Typography key={h} sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted }}>
                                            {h}
                                        </Typography>
                                    ))}
                                </Box>
                                {shown.slice(0, SHOWN).map(({ v, points }) => {
                                    const due = v.dueDate ? new Date(v.dueDate) : null;
                                    const overdue = !!due && due < target.day;
                                    return (
                                        <Box key={v.id} sx={{ display: "grid", gridTemplateColumns: GRID, alignItems: "center", padding: "7px 11px", borderTop: `1px solid ${T.borderSoft}` }}>
                                            <Typography sx={{ fontSize: "12px", fontWeight: overdue ? 800 : 600, color: overdue ? T.red : T.ink2 }}>
                                                {due
                                                    ? due.toLocaleDateString("en-GB", {
                                                          day: "2-digit",
                                                          month: "short",
                                                          year: "numeric",
                                                      })
                                                    : "—"}
                                            </Typography>
                                            <Typography sx={{ fontSize: "12.5px", color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                                {v.accountName || "—"}
                                            </Typography>
                                            <Typography sx={{ fontSize: "11.5px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                                {v.subActivity || v.name}
                                                {v.locationGroup ? ` · ${v.locationGroup}` : ""}
                                            </Typography>
                                            <Typography sx={{ fontSize: "12px", fontWeight: 800, color: points == null ? T.amber : T.purpleDeep, fontVariantNumeric: "tabular-nums" }}>
                                                {points == null ? "—" : fmtPoints(points)}
                                            </Typography>
                                            <Typography sx={{ fontSize: "11.5px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {v.allocatedToName || "unallocated"}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                {shown.length === 0 && (
                                    <Typography sx={{ fontSize: "12.5px", color: T.muted, padding: "12px 11px" }}>
                                        {tab === "mine"
                                            ? `Nothing owed by ${firstName} in this window — try All visit pool.`
                                            : "Nothing owed in this window."}
                                    </Typography>
                                )}
                                {shown.length > SHOWN && (
                                    <Typography sx={{ fontSize: "11px", color: T.muted2, padding: "8px 11px", borderTop: `1px solid ${T.borderSoft}` }}>
                                        Showing the {SHOWN} soonest of {shown.length}. The Visit pool page has the rest.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "16px" }}>
                    {target.existing.length === 0 && (
                        <Box component="button" onClick={onAddAdmin} title="Adds it straight to the board" sx={btn("dark")}>
                            Add admin day
                        </Box>
                    )}
                    <Typography sx={{ fontSize: "11px", color: T.muted2, ml: "4px" }}>
                        Points assume an on-site visit — unbooked work has no on-site/remote flag. Book in
                        Salesforce; there is no visit-write API yet.
                    </Typography>
                    <Box sx={{ marginLeft: "auto" }}>
                        <Box component="button" onClick={onClose} sx={btn("plain")}>
                            Back
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default DayPlannerModal;
